import { createSignal, Show } from "solid-js";
import { SessionAPI, updateSession } from "../api/sessions";
import { ContactType } from "../types/contact";
import { useProfile } from "../contexts/ProfileContext";
import { getContactTypeMeta, resolveContactType } from "../config/contactTypes";

interface Session {
  id: string;
  title: string;
  contactType: ContactType;
  status: "draft" | "in_progress" | "completed";
  createdAt: string;
  duration: number; // in seconds
  languageDetected: string | null;
  preview: string;
  participantCount: number;
}

interface SessionItemProps {
  session: SessionAPI;
  onOpen: (sessionId: string) => void;
  onDelete: (sessionId: string) => void;
  onTitleUpdated?: (sessionId: string, newTitle: string) => void;
}

const SessionItem = (props: SessionItemProps) => {
  const [showActions, setShowActions] = createSignal(false);
  const [isEditingTitle, setIsEditingTitle] = createSignal(false);
  const [editTitle, setEditTitle] = createSignal(props.session.title);
  const [savingTitle, setSavingTitle] = createSignal(false);

  let titleInputRef: HTMLInputElement | undefined;

  // Format duration from seconds to readable format
  const formatDuration = (seconds: number): string => {
    if (seconds === 0) return "Not started";
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
      const remainingMinutes = minutes % 60;
      return `${hours}h ${remainingMinutes}m`;
    }
    return `${minutes}m`;
  };

  // Format date to readable format
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    } else if (diffInHours < 24 * 7) {
      return date.toLocaleDateString([], {
        weekday: "short",
        month: "short",
        day: "numeric",
      });
    } else {
      return date.toLocaleDateString([], {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    }
  };

  // Get status color and text
  const getStatusDisplay = (status: string) => {
    switch (status) {
      case "completed":
        return { color: "text-green-600 bg-green-100", text: "Completed" };
      case "in_progress":
        return { color: "text-blue-600 bg-blue-100", text: "In Progress" };
      case "draft":
        return { color: "text-gray-600 bg-gray-100", text: "Draft" };
      default:
        return { color: "text-gray-600 bg-gray-100", text: status };
    }
  };

  const statusDisplay = () => getStatusDisplay(props.session.status);
  const { profile } = useProfile();
  const resolvedContactType = () => resolveContactType(props.session.contactType);
  const contactTypeDisplay = () =>
    getContactTypeMeta(resolvedContactType(), profile());
  // Title editing functions
  const startEditingTitle = (e: Event) => {
    e.stopPropagation(); // Prevent opening the session
    setIsEditingTitle(true);
    setEditTitle(props.session.title);
    setTimeout(() => {
      titleInputRef?.focus();
      titleInputRef?.select();
    }, 0);
  };

  const saveTitle = async () => {
    const newTitle = editTitle().trim();
    if (!newTitle || newTitle === props.session.title) {
      setIsEditingTitle(false);
      setEditTitle(props.session.title);
      return;
    }

    setSavingTitle(true);
    try {
      await updateSession(props.session.id, { title: newTitle });
      setIsEditingTitle(false);
      props.onTitleUpdated?.(props.session.id, newTitle);
      console.log("Session title updated successfully");
    } catch (error) {
      console.error("Failed to update session title:", error);
      setEditTitle(props.session.title); // Revert on error
    } finally {
      setSavingTitle(false);
    }
  };

  const cancelEditingTitle = () => {
    setIsEditingTitle(false);
    setEditTitle(props.session.title);
  };

  const handleTitleKeyDown = (e: KeyboardEvent) => {
    e.stopPropagation();
    if (e.key === "Enter") {
      e.preventDefault();
      saveTitle();
    } else if (e.key === "Escape") {
      e.preventDefault();
      cancelEditingTitle();
    }
  };

  const handleClickOutside = () => {
    setShowActions(false);
  };

  return (
    <div class="p-4 hover:bg-gray-50 transition-colors relative group">
      <div class="flex items-start justify-between">
        {/* Main Content */}
        <div
          class="flex-1 cursor-pointer"
          onClick={() => props.onOpen(props.session.id)}
        >
          {/* Title and Type */}
          <div class="flex items-center gap-3 mb-2">
            <span class="text-lg">{contactTypeDisplay().icon}</span>
            <Show
              when={isEditingTitle()}
              fallback={
                <div class="flex items-center gap-2 flex-1">
                  <h3 class="text-lg font-semibold text-gray-900 hover:text-blue-600 transition-colors flex-1">
                    {props.session.title}
                  </h3>
                  <button
                    onClick={startEditingTitle}
                    class="text-gray-400 hover:text-blue-600 transition-colors p-1 rounded opacity-0 group-hover:opacity-100"
                    title="Edit title"
                  >
                    <svg
                      class="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                      />
                    </svg>
                  </button>
                </div>
              }
            >
              <div class="flex items-center gap-2 flex-1">
                <input
                  ref={titleInputRef}
                  type="text"
                  value={editTitle()}
                  onInput={(e) => setEditTitle(e.currentTarget.value)}
                  onKeyDown={handleTitleKeyDown}
                  onBlur={saveTitle}
                  disabled={savingTitle()}
                  class="text-lg font-semibold text-gray-900 bg-transparent border-b-2 border-blue-500 outline-none flex-1 min-w-0"
                  placeholder="Enter session title..."
                />
                <div class="flex items-center gap-1">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      saveTitle();
                    }}
                    disabled={savingTitle() || !editTitle().trim()}
                    class="text-green-600 hover:text-green-700 disabled:text-gray-400 p-1 rounded transition-colors"
                    title="Save"
                  >
                    <Show
                      when={!savingTitle()}
                      fallback={
                        <svg
                          class="w-4 h-4 animate-spin"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            class="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            stroke-width="4"
                          />
                          <path
                            class="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                          />
                        </svg>
                      }
                    >
                      <svg
                        class="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </Show>
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      cancelEditingTitle();
                    }}
                    disabled={savingTitle()}
                    class="text-red-600 hover:text-red-700 disabled:text-gray-400 p-1 rounded transition-colors"
                    title="Cancel"
                  >
                    <svg
                      class="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </Show>
          </div>

          {/* Metadata */}
          <div class="flex items-center gap-4 text-sm text-gray-600 mb-2">
            <span>{formatDate(props.session.createdAt)}</span>
            <span>•</span>
            <span>{formatDuration(props.session.duration)}</span>
            <span>•</span>
            <span
              class={`px-2 py-1 rounded-full text-xs font-medium ${
                statusDisplay().color
              }`}
            >
              {statusDisplay().text}
            </span>
            <span>•</span>
            <span
              class={`text-xs font-medium px-2 py-0.5 rounded-full ${contactTypeDisplay().colorClass}`}
            >
              {contactTypeDisplay().label}
            </span>
          </div>

          {/* Preview Text */}
          <Show when={props.session.preview}>
            <p class="text-gray-700 text-sm leading-relaxed line-clamp-2 mb-1">
              {props.session.preview}
            </p>
          </Show>

          {/* Summary (from meeting insights) */}
          <Show when={props.session.summary}>
            <p class="text-gray-600 text-xs leading-snug line-clamp-1 italic">
              {props.session.summary}
            </p>
          </Show>

          {/* Additional Info */}
          <div class="flex items-center gap-4 text-xs text-gray-500 flex-wrap">
            <Show when={props.session.caseId}>
              <span>📋 Case: {props.session.caseId}</span>
            </Show>
            <Show when={props.session.respondentName}>
              <span>👤 Respondent: {props.session.respondentName}</span>
            </Show>
            <Show when={props.session.interviewerName}>
              <span>🎤 Interviewer: {props.session.interviewerName}</span>
            </Show>
            <Show when={props.session.participantCount > 0}>
              <span>
                👥 {props.session.participantCount} participant
                {props.session.participantCount !== 1 ? "s" : ""}
              </span>
            </Show>
            <Show when={props.session.languageDetected}>
              <span>🌐 {props.session.languageDetected!.toUpperCase()}</span>
            </Show>
          </div>
        </div>

        {/* Actions */}
        <div class="ml-4 flex-shrink-0 relative">
          {/* Mobile: Dropdown Menu */}
          <div class="sm:hidden">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowActions(!showActions());
              }}
              class="p-2 rounded-full hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <svg
                class="w-5 h-5 text-gray-500"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
              </svg>
            </button>

            <Show when={showActions()}>
              <div class="absolute right-0 top-full mt-1 w-32 bg-white border border-gray-200 rounded-md shadow-lg z-10">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    props.onOpen(props.session.id);
                    setShowActions(false);
                  }}
                  class="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  Open
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    props.onDelete(props.session.id);
                    setShowActions(false);
                  }}
                  class="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-gray-100"
                >
                  Delete
                </button>
              </div>
            </Show>
          </div>

          {/* Desktop: Inline Buttons */}
          <div class="hidden sm:flex gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                props.onOpen(props.session.id);
              }}
              class="px-3 py-1 text-sm font-medium text-blue-600 border border-blue-600 rounded-md hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
            >
              Open
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                props.onDelete(props.session.id);
              }}
              class="px-3 py-1 text-sm font-medium text-red-600 border border-red-600 rounded-md hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 transition-colors"
            >
              Delete
            </button>
          </div>
        </div>
      </div>

      {/* Click outside to close mobile actions */}
      <Show when={showActions()}>
        <div class="fixed inset-0 z-0" onClick={handleClickOutside} />
      </Show>
    </div>
  );
};

export default SessionItem;
